<template>
  <div>
    <div class="centered-header">
      <h2 class="ui header" style="color: #092442;">
        <i class="bed icon"></i>
        <div class="content">
          Patient Information
        </div>
      </h2>
    </div>

    <div v-if="loading" class="ui active centered inline loader">
      <div class="ui text loader">{{ $t('loading') }}...</div>
    </div>
    <div class="ui form" id="patient-form" v-else>
      <h4 class="ui horizontal divider header">
        <i class="question circle outline icon"></i>
        Main Information
      </h4>
      <div class="field">
        <label for="height">Code</label>
        <div class="ui input">
          <input
            type="text"
            placeholder="Code"
            v-model="patient.code"
            name="code"
          />
        </div>
      </div>

      <div class="three required fields"> 
        <div class="field">
          <label for="firstName">{{ $t('firstName') }}</label>
          <div class="ui input">
            <input
              type="text"
              :placeholder="$t('firstName')"
              v-model="patient.firstName"
              name="firstName"
            />
          </div>
        </div>

        <div class="field">
          <label for="middleName">{{ $t('middleName') }}</label>
          <div class="ui input">
            <input
              type="text"
              :placeholder="$t('name')"
              v-model="patient.middleName"
              name="middleName"
            />
          </div>
        </div>

        <div class="field">
          <label for="lastName">{{ $t('lastName') }}</label>
          <div class="ui input">
            <input
              type="text"
              :placeholder="$t('lastName')"
              v-model="patient.lastName"
              name="lastName"
            />
          </div>
        </div>
      </div>

      <div class="two fields">
        <div class="required field">
          <label for="cell_phone">{{ $t('mobile') }}</label>
          <div class="ui input left icon">
            <i class="mobile icon"></i>
            <input
              type="text"
              :placeholder="$t('mobile')"
              v-model="patient.cell_phone"
              name="cellPhone"
            />
          </div>
        </div>
        
        <div class="field">
          <label for="telephone">{{ $t('phone') }}</label>
          <div class="ui input left icon">
            <i class="phone icon"></i>
            <input
              type="text"
              :placeholder="$t('phone')"
              v-model="patient.telephone"
              name="homePhone"
            />
          </div>
        </div>
      </div>

      <div class="two fields">
        <div class="field">
          <label for="date_of_birth">{{ $t('dateOfBirth') }}</label>
          <div
            style="text-align: center"
            class="ui calendar"
            :id="'date-of-birth'"
          >
            <div class="ui input left icon">
              <i class="calendar icon"></i>
              <input
                type="text"
                :placeholder="$t('dateOfBirth')"
                readonly=""
                name="day"
              />
            </div>
          </div>
        </div>

        <div class="required field">
          <label for="gender">{{ $t('chooseGender') }}</label>
          <select
            class="ui fluid search dropdown"
            id="patient-gender-search"
            v-model.number="patient.gender"
            name="gender"
          >
            <option value="">Patient Gender</option>
            <option :value="1">{{ $t('male') }}</option>
            <option :value="2">{{ $t('female') }}</option>
          </select>
        </div>
      </div>

      <div class="two fields" v-if="useInventory">
        <div class="field">
          <label for="weight"> {{ $t('weight') }}</label>
          <div class="ui input">
            <input
              type="number"
              :placeholder="$t('weight')"
              v-model="patient.weight"
              name="weight"
            />
          </div>
        </div>

        <div class="field">
          <label for="height">Height</label>
          <div class="ui input">
            <input
              type="number"
              placeholder="Height"
              v-model="patient.patient_details.height"
              name="height"
            />
          </div>
        </div>
      </div>

      <div class="field">
        <label for="addressArea">Address Area</label>
        <select
          class="ui fluid search dropdown"
          id="patient-addressArea-search"
          v-model.number="patient.patient_area_id"
          name="addressArea"
        >
          <option value="">Select Area</option>
          <option
            v-for="address in PatientAreas.DATA"
            :value="address.id"
            :key="address.is"
            >{{ address.name }}</option
          >
        </select>
      </div>

      <h4 class="ui horizontal divider header">
        <i class="question circle outline icon"></i>
        For Females
      </h4>

      <div class="two fields">
        <div class="field">
          <div class="ui checkbox">
            <input
              type="checkbox"
              v-model="patient.patient_details.breastfeeding"
              name="breastfeeding"
            />
            <label for="breastfeeding" class="checkboxLabel"
              >Breast Feeding</label
            >
          </div>
        </div>

        <div class="field">
          <div class="ui checkbox">
            <input
              type="checkbox"
              v-model="patient.patient_details.pregnant"
              name="pregnant"
            />
            <label for="pregnant" class="checkboxLabel">Pregnant</label>
          </div>
        </div>
      </div>

      <h4 class="ui horizontal divider header">
        <i class="question circle outline icon"></i>
        Other Information
      </h4>

      <div class="field">
        <label for="medical_condition">Medical Conditions</label>
        <div class="ui input">
          <textarea
            rows="2"
            type="text"
            placeholder="medical conditions"
            v-model="patient.patient_details.medical_condition"
            name="medical_condition"
          />
        </div>
      </div>

      <div class="field">
        <label for="medication">Medication</label>
        <div class="ui input">
          <textarea
            rows="2"
            type="text"
            placeholder="medication"
            v-model="patient.patient_details.medication"
            name="medication"
          />
        </div>
      </div>

      <div class="field">
        <label for="note">Note</label>
        <div class="ui input">
          <textarea
            rows="2"
            type="text"
            placeholder="Note"
            v-model="patient.patient_details.note"
            name="note"
          />
        </div>
      </div>

      <div class="field">
        <label for="referrer">{{ $t('referrer') }}</label>
        <select
          class="ui fluid dropdown"
          id="patient-referrer-search"
          v-model.number="patient.patient_details.referrer"
          name="referrer"
        >
          <option value="">Select Referrer</option>
          <option value="Family">Family</option>
          <option value="Friends">Friends</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="Twitter">Twitter</option>
          <option value="Normal Ads">Normal Ads</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div
        class="ui blue right floated right labeled icon submit button"
        style="background-color: #092442"
        :class="{ disabled: isPressed }"
        @click="save()"
      >
        <i class="right arrow icon"></i>
        {{ update ? 'Update Patient' : 'Create Patient' }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      update: false,
      detailsId: null,
      loading: false,
      isPressed: false,
      patient: {
        name: null,
        firstName: null,
        middleName: null,
        lastName: null,
        telephone: null,
        cell_phone: null,
        date_of_birth: null,
        weight: null,
        gender: 2,
        code: null,
        patient_area_id: '',
        patient_details: {
          breastfeeding: false,
          pregnant: false,
          height: null,
          medical_condition: '',
          medication: '',
          note: '',
          referrer: '',
        }
      }
    };
  },
  mounted() {
    if (this.$route.params && !isNaN(this.$route.params.id)) {
      // update case
      this.update = true;
      this.loading = true;
      this.$http
        .get(`patients/${this.$route.params.id}`)
        .then(res => {
          if (res && res.data) {
            delete res.data.reservations;
            this.patient = res.data.patient;
            this.formlatePatient(this.patient);
            this.detailsId = this.patient.patient_details.id;
          }
          this.loading = false;
          this.initGUI();
        })
        .catch(error => console.log(error));
    } else {
      // create case
      this.update = false;
      this.initGUI();
    }
  },
  computed: {
    useInventory() {
      return this.permissions.invetory_permissions.used
    },
    patientDetails() {
      return this.patient.patient_details &&
        this.patient.patient_details.length > 0
        ? this.patient.patient_details[0].details
          ? this.patient.patient_details[0].details
          : {}
        : {};
    }
  },
  methods: {
    formlatePatient(patientData) {
      const names = this.getPatientDividedName(this.adjustName(this.patient.name));

      this.patient = {
        ...patientData,
        firstName: names.firstName,
        middleName: names.middleName,
        lastName: names.lastName,
        patient_details: {
          id: this.patient.patient_details[0].id,
          breastfeeding: this.patientDetails.breastfeeding || false,
          pregnant: this.patientDetails.pregnant || false,
          height: this.patientDetails.height || null,
          medical_condition: this.patientDetails.medical_condition || '',
          medication: this.patientDetails.medication || '',
          note: this.patientDetails.note || this.patientDetails.exercise || '',
          referrer: this.patientDetails.referrer,
        }
      };
    },
    initGUI() {
      this.$nextTick(() => {
        $('#patient-gender-search').dropdown(
          'set selected',
          this.patient.gender
        );

        $('#patient-referrer-search').dropdown(
          'set selected',
          this.patient.patient_details.referrer
        );

        $('#patient-addressArea-search').dropdown(
          'set selected',
          this.patient.patient_area_id
        );

        $('#date-of-birth').calendar({
          type: 'date',
          maxDate: new Date(),
          onChange: date => {
            this.patient.date_of_birth = date;
          }
        });

        if (this.patient.date_of_birth) {
          $('#date-of-birth').calendar(
            'set date',
            new Date(this.patient.date_of_birth)
          );
        }

        $('#patient-form').form({
          inline: true,
          on: 'submit',
          fields: {
            firstName: {
              rules: [
                {
                  type: 'empty',
                  prompt: 'Please enter employee first name.'
                }
              ]
            },
            middleName: {
              rules: [
                {
                  type: 'empty',
                  prompt: 'Please enter employee middle name.'
                }
              ]
            },
            lastName: {
              rules: [
                {
                  type: 'empty',
                  prompt: 'Please enter employee last name.'
                }
              ]
            },
            gender: {
              rules: [
                {
                  type: 'empty',
                  prompt: 'Please Choose employee gender.'
                },
                {
                  type: 'integer[1..2]',
                  prompt: 'Please Choose valid option.'
                }
              ]
            },
            cellPhone: {
              rules: [
                {
                  type: 'empty',
                  prompt: 'Please enter employee cell phone.'
                },
                {
                  type: 'integer',
                  prompt: 'Please enter valid cell phone number.'
                },
                {
                  type: 'minLength[11]',
                  prompt: 'Please enter valid cell phone number.'
                }
              ]
            }
          }
        });
      });
    },
    save() {
      if (!this.update) {
        if ($('#patient-form').form('is valid')) {
          this.isPressed = true;
          this.loading = true;
          this.$http
            .post('patients', {
              patient: {
                ...this.patient,
                name:
                  this.patient.firstName +
                  '/' +
                  this.patient.middleName +
                  '/' +
                  this.patient.lastName
              }
            })
            .then(response => {
              this.loading = false;
              this.navigateTo('Show_Patient', {
                key: 'params',
                value: {
                  id: response.data.id
                }
              });
            })
            .catch(() => {
              this.isPressed = false;
            });
        }
      } else {
        let sendData = {
          ...this.patient,
          name:
            this.patient.firstName +
            '/' +
            this.patient.middleName +
            '/' +
            this.patient.lastName,
          patient_details: {
            id: this.patient.patient_details.id,
            details: {
              ...this.patient.patient_details,
              id: undefined
            }
          }
        };
        if ($('#patient-form').form('is valid')) {
          this.isPressed = true;
          this.loading = true;
          this.$http
            .put(`patients/${this.patient.id}`, {
              patient: sendData
            })
            .then(response => {
              this.loading = false;
              this.navigateTo('Show_Patient', {
                key: 'params',
                value: {
                  id: response.data.id
                }
              });
            })
            .catch(() => {
              this.isPressed = false;
            });
        }
      }
    }
  }
};
</script>

<style scoped>
.centered-header {
  display: flex;
  justify-content: center;
}
.checkboxLabel {
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.92857143em;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/components/patients/NewPatient.vue